/**
 * repositories/betaApplicationStatusHistoryRepository.ts
 * Beta Application Status History table data access layer
 *
 * Architecture: WU1.5 Task 4 - BetaApplicationStatusHistory Repository & Service Refactor
 *
 * Purpose: Centralize all data access for beta_application_status_history table,
 * including JOIN queries with beta_applications. Handles mapping between
 * database snake_case and application camelCase at the boundary.
 *
 * APPEND-ONLY PATTERN: Status history is audit data - no update() or delete() methods.
 * Records are immutable once created to preserve audit trail integrity.
 *
 * ========================================================================
 * JOIN PATTERN
 * ========================================================================
 *
 * PostgREST nested selects behave like LEFT JOINs:
 * - Joined data may be null if FK is invalid
 * - Use assertNotNull() for required joins
 * - Use explicit column projections (no *)
 * - Map base row with toDomain(), joined data with map*Join()
 *
 * ========================================================================
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, throwIfError, assertNotNull } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type { BetaApplicationStatusHistory, BetaApplicationStatus } from '@/types/supabase';

// ============================================================================
// JOIN DOMAIN TYPES (Local to this repository)
// ============================================================================

/**
 * Beta application JOIN domain type (camelCase)
 * Used in repository return types for joined application data
 */
interface BetaAppJoinDomain {
  id: string;
  email: string;
  fullName: string;
  status: BetaApplicationStatus;
}

// ============================================================================
// PROJECTION CONSTANTS
// ============================================================================

/** Column projection for status history records */
const HISTORY_PROJECTION = 'id,application_id,previous_status,new_status,changed_by,admin_notes,changed_at,created_at,updated_at' as const;

/** Column projection for joined application details */
const APP_JOIN_PROJECTION = 'id,email,full_name,status' as const;

// ============================================================================
// WIRE TYPES
// ============================================================================

/** Wire type for beta_application_status_history table (snake_case from database) */
type HistoryRow = Database['public']['Tables']['beta_application_status_history']['Row'];

/** Wire type for beta_applications JOIN (snake_case from database) */
type BetaAppJoinWire = Pick<
  Database['public']['Tables']['beta_applications']['Row'],
  'id' | 'email' | 'full_name' | 'status' | 'created_at'
>;

/** Wire type for beta_applications summary JOIN (snake_case from database) */
type BetaAppSummaryWire = Pick<
  Database['public']['Tables']['beta_applications']['Row'],
  'email' | 'status' | 'created_at'
>;

// ============================================================================
// RETURN TYPES
// ============================================================================

/**
 * Status history with joined application details
 * Used when fetching history records with their associated application info
 */
export interface StatusHistoryWithApplication {
  /** Status history record (camelCase) */
  history: BetaApplicationStatusHistory;
  /** Associated application information (camelCase) */
  application: BetaAppJoinDomain;
}

/**
 * Status change statistics
 * Aggregated data for reporting and analytics
 */
export interface StatusChangeStats {
  /** Total number of status changes */
  totalChanges: number;
  /** Changes grouped by status */
  changesByStatus: Record<string, number>;
  /** Changes grouped by admin */
  changesByAdmin: Record<string, number>;
  /** Changes grouped by date (YYYY-MM-DD) */
  changesByDate: Record<string, number>;
}

/**
 * List options for filtering and pagination
 */
export interface ListHistoryOptions {
  /** Filter by application ID */
  applicationId?: string;
  /** Filter by admin who made the change */
  changedBy?: string;
  /** Filter by date range */
  dateRange?: {
    start: string;
    end: string;
  };
  /** Maximum number of records to return */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
  /** Include total count (adds overhead) */
  withTotal?: boolean;
}

// ============================================================================
// JOIN MAPPING UTILITIES (Local to this repository)
// ============================================================================

/**
 * Map beta_applications JOIN result to camelCase domain type
 *
 * @param raw - Beta application row from JOIN (snake_case)
 * @returns Beta application domain object (camelCase)
 */
function mapBetaApplicationJoin(raw: BetaAppJoinWire): BetaAppJoinDomain {
  return {
    id: raw.id,
    email: raw.email,
    fullName: raw.full_name,
    status: raw.status as BetaApplicationStatus
  };
}

// ============================================================================
// REPOSITORY
// ============================================================================

/**
 * Beta Application Status History Repository
 *
 * Provides data access for status history with JOIN support for beta_applications.
 * All methods return camelCase domain types.
 *
 * IMPORTANT: This repository implements append-only pattern for audit data.
 * No update() or delete() methods are provided to preserve audit trail integrity.
 */
export const betaApplicationStatusHistoryRepository = {
  /**
   * Get a single status history record by ID
   *
   * @param id - Status history record ID
   * @returns Promise resolving to history record or null if not found
   * @throws Error if query fails
   *
   * @example
   * const history = await betaApplicationStatusHistoryRepository.getById('uuid');
   */
  async getById(id: string): Promise<BetaApplicationStatusHistory | null> {
    const result = await supabase
      .from('beta_application_status_history')
      .select(HISTORY_PROJECTION)
      .eq('id', id)
      .maybeSingle<HistoryRow>();

    const data = throwIfError(result);
    if (!data) return null;

    return toDomain<BetaApplicationStatusHistory>(data);
  },

  /**
   * List status history records with optional filtering and pagination
   *
   * Returns history records ordered by changedAt DESC (newest first).
   * Supports filtering by application, admin, and date range.
   *
   * @param opts - Filtering and pagination options
   * @returns Promise resolving to array of history records with optional total count
   * @throws Error if query fails
   *
   * @example
   * const { items, total } = await betaApplicationStatusHistoryRepository.list({
   *   applicationId: 'uuid',
   *   limit: 10,
   *   withTotal: true
   * });
   */
  async list(opts: ListHistoryOptions = {}): Promise<{
    items: BetaApplicationStatusHistory[];
    total?: number;
  }> {
    let query = supabase
      .from('beta_application_status_history')
      .select(HISTORY_PROJECTION, opts.withTotal ? { count: 'exact' } : undefined)
      .order('changed_at', { ascending: false });

    // Apply filters
    if (opts.applicationId) {
      query = query.eq('application_id', opts.applicationId);
    }
    if (opts.changedBy) {
      query = query.eq('changed_by', opts.changedBy);
    }
    if (opts.dateRange) {
      query = query
        .gte('changed_at', opts.dateRange.start)
        .lte('changed_at', opts.dateRange.end);
    }

    // Apply pagination
    if (opts.limit !== undefined) {
      query = query.limit(opts.limit);
    }
    if (opts.offset !== undefined) {
      query = query.range(opts.offset, opts.offset + (opts.limit || 50) - 1);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    const items = (data || []).map((row) =>
      toDomain<BetaApplicationStatusHistory>(row)
    );

    return {
      items,
      ...(opts.withTotal && count !== null ? { total: count } : {})
    };
  },

  /**
   * Create a new status history record
   *
   * Converts camelCase input to snake_case for database insertion.
   * Returns the created record in camelCase.
   *
   * @param data - History data (excluding id, createdAt, updatedAt)
   * @returns Promise resolving to created history record
   * @throws Error if creation fails
   *
   * @example
   * const history = await betaApplicationStatusHistoryRepository.create({
   *   applicationId: 'uuid',
   *   previousStatus: 'pending',
   *   newStatus: 'approved',
   *   changedBy: 'admin@example.com',
   *   adminNotes: 'Approved after review',
   *   changedAt: new Date().toISOString()
   * });
   */
  async create(
    data: Omit<BetaApplicationStatusHistory, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<BetaApplicationStatusHistory> {
    const wireData = toWire(data) as Database['public']['Tables']['beta_application_status_history']['Insert'];

    const result = await supabase
      .from('beta_application_status_history')
      .insert(wireData)
      .select(HISTORY_PROJECTION)
      .single<HistoryRow>();

    const created = throwIfError(result);
    return toDomain<BetaApplicationStatusHistory>(created);
  },

  /**
   * Get status history for a specific application (without JOINs)
   *
   * Returns history records for a single application, ordered newest first.
   * For history with application details, use listByApplicationId().
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to array of history records
   * @throws Error if query fails
   *
   * @example
   * const history = await betaApplicationStatusHistoryRepository.getByApplicationId('uuid');
   */
  async getByApplicationId(
    applicationId: string
  ): Promise<BetaApplicationStatusHistory[]> {
    const result = await supabase
      .from('beta_application_status_history')
      .select(HISTORY_PROJECTION)
      .eq('application_id', applicationId)
      .order('changed_at', { ascending: false });

    const data = throwIfError(result);
    if (!data) return [];

    return data.map((row) => toDomain<BetaApplicationStatusHistory>(row));
  },

  /**
   * Get most recent status change for an application
   *
   * Returns the latest status change record for an application.
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to most recent history record or null
   * @throws Error if query fails
   *
   * @example
   * const latest = await betaApplicationStatusHistoryRepository.getMostRecent('uuid');
   */
  async getMostRecent(
    applicationId: string
  ): Promise<BetaApplicationStatusHistory | null> {
    const result = await supabase
      .from('beta_application_status_history')
      .select(HISTORY_PROJECTION)
      .eq('application_id', applicationId)
      .order('changed_at', { ascending: false })
      .limit(1)
      .maybeSingle<HistoryRow>();

    const data = throwIfError(result);
    if (!data) return null;

    return toDomain<BetaApplicationStatusHistory>(data);
  },

  /**
   * Count status changes for an application
   *
   * Returns the total number of status changes for a given application.
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to count
   * @throws Error if query fails
   *
   * @example
   * const count = await betaApplicationStatusHistoryRepository.getCount('uuid');
   */
  async getCount(applicationId: string): Promise<number> {
    const result = await supabase
      .from('beta_application_status_history')
      .select('*', { count: 'exact', head: true })
      .eq('application_id', applicationId);

    const { count, error } = result;
    if (error) throw error;

    return count || 0;
  },

  /**
   * Get status change statistics with optional date filtering
   *
   * Returns aggregated statistics for reporting and analytics.
   * DB-side aggregation is performed for efficiency.
   *
   * @param dateRange - Optional date range filter
   * @returns Promise resolving to statistics object
   * @throws Error if query fails
   *
   * @example
   * const stats = await betaApplicationStatusHistoryRepository.getStats({
   *   start: '2025-01-01',
   *   end: '2025-12-31'
   * });
   */
  async getStats(dateRange?: {
    start: string;
    end: string;
  }): Promise<StatusChangeStats> {
    let query = supabase
      .from('beta_application_status_history')
      .select('new_status,changed_by,changed_at');

    if (dateRange) {
      query = query
        .gte('changed_at', dateRange.start)
        .lte('changed_at', dateRange.end);
    }

    const result = await query;
    const data = throwIfError(result);

    const stats: StatusChangeStats = {
      totalChanges: data?.length || 0,
      changesByStatus: {},
      changesByAdmin: {},
      changesByDate: {}
    };

    data?.forEach((item) => {
      // Count by status
      const status: string = item.new_status || 'unknown';
      stats.changesByStatus[status] = (stats.changesByStatus[status] || 0) + 1;

      // Count by admin
      const admin: string = item.changed_by || 'unknown';
      stats.changesByAdmin[admin] = (stats.changesByAdmin[admin] || 0) + 1;

      // Count by date (YYYY-MM-DD)
      const dateStr = item.changed_at ? item.changed_at.split('T')[0] : null;
      const date: string = dateStr || 'unknown';
      stats.changesByDate[date] = (stats.changesByDate[date] || 0) + 1;
    });

    return stats;
  },

  // ========================================================================
  // JOIN METHODS
  // ========================================================================

  /**
   * List status history for a specific application with application details
   *
   * Returns all status changes for an application with joined application info.
   * Ordered newest first.
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to array of history with application details
   * @throws Error if query fails or joined application is missing
   *
   * @example
   * const history = await betaApplicationStatusHistoryRepository.listByApplicationId('uuid');
   * // Returns: [{ history: {...}, application: { id, email, fullName, status } }]
   */
  async listByApplicationId(
    applicationId: string
  ): Promise<StatusHistoryWithApplication[]> {
    const result = await supabase
      .from('beta_application_status_history')
      .select(`${HISTORY_PROJECTION}, beta_applications(${APP_JOIN_PROJECTION})`)
      .eq('application_id', applicationId)
      .order('changed_at', { ascending: false });

    const data = throwIfError(result);
    if (!data) return [];

    return data.map((row) => {
      // Note: PostgREST nested selects behave like LEFT JOINs
      // Nulls are expected if FK is invalid
      const app = assertNotNull(row.beta_applications, 'Missing joined application');

      return {
        history: toDomain<BetaApplicationStatusHistory>(row),
        application: mapBetaApplicationJoin(app as BetaAppJoinWire)
      };
    });
  },

  /**
   * List recent status changes across all applications
   *
   * Returns recent status changes with application details, optionally filtered by admin.
   * Useful for admin dashboard views.
   *
   * @param limit - Maximum number of records to return (default: 50)
   * @param adminFilter - Optional admin username to filter by
   * @returns Promise resolving to array of recent history with application details
   * @throws Error if query fails or joined application is missing
   *
   * @example
   * const recent = await betaApplicationStatusHistoryRepository.listRecent(10, 'admin@example.com');
   * // Returns: [{ history: {...}, application: {...} }] (newest 10 by this admin)
   */
  async listRecent(
    limit: number = 50,
    adminFilter?: string
  ): Promise<StatusHistoryWithApplication[]> {
    let query = supabase
      .from('beta_application_status_history')
      .select(`${HISTORY_PROJECTION}, beta_applications(${APP_JOIN_PROJECTION})`)
      .order('changed_at', { ascending: false })
      .limit(limit);

    if (adminFilter) {
      query = query.eq('changed_by', adminFilter);
    }

    const result = await query;
    const data = throwIfError(result);
    if (!data) return [];

    return data.map((row) => {
      // Note: PostgREST nested selects behave like LEFT JOINs
      const app = assertNotNull(row.beta_applications, 'Missing joined application');

      return {
        history: toDomain<BetaApplicationStatusHistory>(row),
        application: mapBetaApplicationJoin(app as BetaAppJoinWire)
      };
    });
  },

  /**
   * Get status summaries for all applications
   *
   * Returns aggregated status change information grouped by application.
   * Used for reporting and analytics.
   *
   * @returns Promise resolving to array of status summaries
   * @throws Error if query fails
   *
   * @example
   * const summaries = await betaApplicationStatusHistoryRepository.listSummaries();
   * // Returns: Array of summary objects with change counts and timing
   */
  async listSummaries(): Promise<
    Array<{
      applicationId: string;
      applicantEmail: string;
      currentStatus: string;
      submittedAt: string;
      lastChangedAt: string;
      lastChangedBy: string;
      totalChanges: number;
    }>
  > {
    const result = await supabase
      .from('beta_application_status_history')
      .select(`application_id,changed_at,changed_by,beta_applications(email,status,created_at)`)
      .order('changed_at', { ascending: false });

    const data = throwIfError(result);
    if (!data) return [];

    // Group by application_id and create summaries
    const summaryMap = new Map<
      string,
      {
        applicationId: string;
        applicantEmail: string;
        currentStatus: string;
        submittedAt: string;
        lastChangedAt: string;
        lastChangedBy: string;
        totalChanges: number;
      }
    >();

    data.forEach((item) => {
      const applicationId = item.application_id;

      if (!summaryMap.has(applicationId)) {
        // Note: PostgREST nested selects behave like LEFT JOINs
        const app = assertNotNull(
          item.beta_applications,
          'Missing joined application'
        ) as BetaAppSummaryWire;

        summaryMap.set(applicationId, {
          applicationId,
          applicantEmail: app.email,
          currentStatus: app.status ?? 'pending',
          submittedAt: app.created_at ?? '',
          lastChangedAt: item.changed_at ?? '',
          lastChangedBy: item.changed_by ?? '',
          totalChanges: 1
        });
      } else {
        // Increment change count for existing application
        const summary = summaryMap.get(applicationId);
        if (summary) {
          summary.totalChanges += 1;

          // Update last changed info if this is an earlier record
          if (item.changed_at > summary.lastChangedAt) {
            summary.lastChangedAt = item.changed_at;
            summary.lastChangedBy = item.changed_by;
          }
        }
      }
    });

    return Array.from(summaryMap.values());
  }
};
